import React from 'react';
import styled from 'styled-components';
import Popover from '../../components/Popover';
import RatingBreakdown from './RatingBreakdown';
import {
  getRatingDistribution,
  IRatingDistributionFields
} from '../../section/Common/utils/ratingDistribution';
import {
  AVERAGE_RATING_LABEL,
  NO_RATING_PLACEHOLDER,
  POPOVER_BORDER_RADIUS,
  POPOVER_PADDING,
  POPOVER_WIDTH,
  RATING_POPOVER_CLASS
} from './constants';

interface IRatingDistributionProps {
  rating?: number | string | null;
  book?: IRatingDistributionFields | null;
}

// '&&' outweighs the shared Popover's own rules regardless of stylesheet order.
const RatingPopover = styled(Popover)`
  && .ant-popover-inner {
    /* The shared Popover's 8px is itself !important. */
    border-radius: ${POPOVER_BORDER_RADIUS}px !important;
  }

  && .ant-popover-inner-content {
    width: ${POPOVER_WIDTH}px;
    padding: ${POPOVER_PADDING}px;
  }
`;

// Sits above both the trigger and the portalled overlay in the React tree.
const PopoverBoundary = styled.span`
  display: inline-flex;
`;

const Value = styled.span`
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const RatingValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

// antd portals the overlay, but React still bubbles its events to the row's onClick.
// Trigger clicks pass through, so the cell still opens the title overlay.
const stopOverlayEvents = (event: React.SyntheticEvent) => {
  const { target } = event;

  if (target instanceof Element && target.closest(`.${RATING_POPOVER_CLASS}`)) {
    event.stopPropagation();
  }
};

// Neutral here - the accent is reserved for the popover.
const Star = styled.span`
  font-size: 12px;
  line-height: 1;
`;

/**
 * The 'Amzn Rating' cell: the average, with the 1-5 star breakdown behind a popover. An
 * unrated title gets a placeholder and no popover; a rated one without a synced breakdown
 * still opens, so the popover can say so rather than showing empty bars.
 */
const RatingDistribution: React.FC<IRatingDistributionProps> = ({ rating, book }) => {
  const average = Number(rating);

  if (rating === null || rating === undefined || rating === '' || !Number.isFinite(average)) {
    return <span>{NO_RATING_PLACEHOLDER}</span>;
  }

  return (
    <PopoverBoundary onClick={stopOverlayEvents} onDoubleClick={stopOverlayEvents}>
      <RatingPopover
        className={RATING_POPOVER_CLASS}
        content={<RatingBreakdown rating={average} distribution={getRatingDistribution(book)} />}
        trigger={['hover', 'focus']}
        placement='bottom'
      >
        <RatingValue
          aria-label={`${average} ${AVERAGE_RATING_LABEL}`}
          data-testid='amzn-rating-value'
          tabIndex={0}
        >
          <Star aria-hidden>&#9733;</Star>
          <Value>{rating}</Value>
        </RatingValue>
      </RatingPopover>
    </PopoverBoundary>
  );
};

export default RatingDistribution;
